import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminCollections.css";
import API_BASE_URL from "../../config/api";

function AdminCollections() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingProduct, setEditingProduct] = useState(null);

  const [editName, setEditName] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editTopPrice, setEditTopPrice] = useState("");
  const [editTag, setEditTag] = useState("");
  const [editCollection, setEditCollection] = useState("CLASSIC");
  const [editDescription, setEditDescription] = useState("");

  const [editImages, setEditImages] = useState({
    image1: null,
    image2: null,
    image3: null,
    image4: null,
  });

  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const normalizeCollection = (value) => {
    const collection = String(value || "")
      .trim()
      .toUpperCase();

    if (collection.includes("PREMIUM")) {
      return "PREMIUM";
    }

    if (collection.includes("ESSENTIAL")) {
      return "ESSENTIAL";
    }

    if (collection.includes("CLASSIC")) {
      return "CLASSIC";
    }

    return "CLASSIC";
  };

  const getImageUrl = (image) => {
    if (!image) return "";

    if (image.startsWith("http")) {
      return image;
    }

    return `${API_BASE_URL}${image}`;
  };

  const formatPrice = (price) => {
    return Number(price || 0).toLocaleString();
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE_URL}/api/products`
      );

      const data = await response.json();

      if (data.success) {
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error("Products fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleEdit = (product) => {
    setEditingProduct(product);

    setEditName(product.name || "");
    setEditPrice(product.price || "");
    setEditTopPrice(product.top_price || "");
    setEditTag(product.tag || "");

    setEditCollection(
      normalizeCollection(product.collection)
    );

    setEditDescription(product.description || "");

    setEditImages({
      image1: null,
      image2: null,
      image3: null,
      image4: null,
    });

    document.body.style.overflow = "hidden";
  };

  const closeEdit = () => {
    setEditingProduct(null);

    setEditName("");
    setEditPrice("");
    setEditTopPrice("");
    setEditTag("");
    setEditCollection("CLASSIC");
    setEditDescription("");

    setEditImages({
      image1: null,
      image2: null,
      image3: null,
      image4: null,
    });

    document.body.style.overflow = "";
  };

  const handleImageChange = (field, file) => {
    if (!file) return;

    setEditImages((previous) => ({
      ...previous,
      [field]: file,
    }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!editingProduct) return;

    const token = localStorage.getItem("adminToken");

    if (!token) {
      navigate("/admin/login");
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("name", editName);
      formData.append("price", editPrice);
      formData.append("topPrice", editTopPrice.trim());
      formData.append("tag", editTag);

      // IMPORTANT:
      // Send selected collection to backend
      formData.append("collection", editCollection);

      formData.append("description", editDescription);
      // IMAGE 1

      if (editImages.image1) {
        formData.append(
          "image1",
          editImages.image1
        );
      }
      // IMAGE 2

      if (editImages.image2) {
        formData.append(
          "image2",
          editImages.image2
        );
      }
      // IMAGE 3

      if (editImages.image3) {
        formData.append(
          "image3",
          editImages.image3
        );
      }
      // IMAGE 4

      if (editImages.image4) {
        formData.append(
          "image4",
          editImages.image4
        );
      }
      // UPDATE API

      const response = await fetch(
        `${API_BASE_URL}/api/products/${editingProduct.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        alert(
          data.message ||
            "Failed to update product"
        );

        return;
      }

      alert(
        "Product updated successfully."
      );

      closeEdit();

      fetchProducts();

    } catch (error) {
      console.error(
        "Update product error:",
        error
      );

      alert(
        "Something went wrong while updating product."
      );

    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${product.name}"?`
    );

    if (!confirmed) return;

    const token = localStorage.getItem("adminToken");

    if (!token) {
      navigate("/admin/login");
      return;
    }

    try {
      setDeletingId(product.id);

      const response = await fetch(
        `${API_BASE_URL}/api/products/${product.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        alert(
          data.message ||
            "Failed to delete product"
        );

        return;
      }

      setProducts((previous) =>
        previous.filter(
          (item) =>
            item.id !== product.id
        )
      );

    } catch (error) {
      console.error(
        "Delete product error:",
        error
      );

      alert(
        "Something went wrong while deleting product."
      );

    } finally {
      setDeletingId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");

    navigate("/admin/login");
  };

  return (
    <main className="tv-admin-collections">

      {/* =====================================================
          SIDEBAR
      ===================================================== */}

      <aside className="tv-admin-sidebar">

        <div className="tv-admin-sidebar-brand">

          <img
            src="/images/logo.png"
            alt="Time Valley"
          />

          <span>
            ADMINISTRATION
          </span>

        </div>

        <nav className="tv-admin-sidebar-nav">

          <button
            onClick={() =>
              navigate(
                "/admin/dashboard"
              )
            }
          >
            <span>01</span>
            Dashboard
          </button>

          <button
            onClick={() =>
              navigate(
                "/admin/products"
              )
            }
          >
            <span>02</span>
            Products
          </button>

          <button className="tv-admin-nav-active">
            <span>03</span>
            Collections
          </button>

          <button>
            <span>04</span>
            Orders
          </button>

          <button>
            <span>05</span>
            Customers
          </button>

        </nav>

        <div className="tv-admin-sidebar-bottom">

          <button
            onClick={handleLogout}
            className="tv-admin-logout"
          >
            <span>↗</span>
            Sign Out
          </button>

        </div>

      </aside>

      {/* =====================================================
          MAIN
      ===================================================== */}

      <section className="tv-admin-collections-main">

        {/* HEADER */}

        <header className="tv-admin-collections-header">

          <div>

            <span className="tv-admin-collections-eyebrow">
              TIME VALLEY WATCHES
            </span>

            <h1>
              Your <span>Collection.</span>
            </h1>

            <p>
              Manage every timepiece currently available
              in your Time Valley collection.
            </p>

          </div>

          <div className="tv-admin-collections-header-actions">

            <div className="tv-admin-collection-count">

              <small>
                TOTAL WATCHES
              </small>

              <strong>
                {products.length}
              </strong>

            </div>

            <button
              className="tv-admin-add-watch"
              onClick={() =>
                navigate(
                  "/admin/products"
                )
              }
            >
              <span>+</span>
              Add New Watch
            </button>

          </div>

        </header>

        {/* =====================================================
            PRODUCTS
        ===================================================== */}

        <section className="tv-admin-products-section">

          <div className="tv-admin-products-heading">

            <div>

              <span>
                THE TIME VALLEY EDIT
              </span>

              <h2>
                All Products
              </h2>

            </div>

            <span className="tv-admin-products-line"></span>

          </div>

          {loading ? (

            <div className="tv-admin-collection-loading">

              <span></span>

              Loading your collection...

            </div>

          ) : products.length === 0 ? (

            <div className="tv-admin-empty">

              <span>01</span>

              <h3>
                Your Collection Is Empty
              </h3>

              <p>
                Start building your Time Valley
                collection by adding your first watch.
              </p>

              <button
                onClick={() =>
                  navigate(
                    "/admin/products"
                  )
                }
              >
                Add Your First Watch →
              </button>

            </div>

          ) : (

            <div className="tv-admin-products-grid">

              {products.map(
                (product, index) => (

                  <article
                    className="tv-admin-product-card"
                    key={product.id}
                  >

                    {/* IMAGE */}

                    <div className="tv-admin-product-image">

                      <span className="tv-admin-product-number">
                        {String(index + 1).padStart(
                          2,
                          "0"
                        )}
                      </span>

                      {/* COLLECTION BADGE */}

                      {product.collection && (
                        <span className="tv-admin-product-tag">
                          {normalizeCollection(
                            product.collection
                          )}
                        </span>
                      )}

                      {/* PRODUCT TAG */}

                      {product.tag && (
                        <span className="tv-admin-product-tag">
                          {product.tag}
                        </span>
                      )}

                      <img
                        src={getImageUrl(
                          product.image1
                        )}
                        alt={product.name}
                      />

                      <div className="tv-admin-product-image-actions">

                        <button
                          onClick={() =>
                            handleEdit(product)
                          }
                        >
                          Edit
                        </button>

                        <button
                          onClick={() =>
                            handleDelete(product)
                          }
                          disabled={
                            deletingId ===
                            product.id
                          }
                        >
                          {deletingId ===
                          product.id
                            ? "Deleting..."
                            : "Delete"}
                        </button>

                      </div>

                    </div>

                    {/* INFO */}

                    <div className="tv-admin-product-info">

                      <span>
                        TIME VALLEY WATCH
                      </span>

                      <h3>
                        {product.name}
                      </h3>

                      <p>
                        {product.description
                          ? product.description
                          : "A refined Time Valley timepiece crafted for timeless elegance."}
                      </p>

                      <div className="tv-admin-product-bottom">

                       <strong>
  Rs {formatPrice(product.price)}
</strong>

                        <button
                          onClick={() =>
                            handleEdit(product)
                          }
                        >
                          Edit Details →
                        </button>

                      </div>

                    </div>

                  </article>

                )
              )}

            </div>

          )}

        </section>

      </section>

      {/* =====================================================
          EDIT MODAL
      ===================================================== */}

      {editingProduct && (

        <div
          className="tv-admin-edit-overlay"
          onMouseDown={(e) => {

            if (
              e.target.className ===
              "tv-admin-edit-overlay"
            ) {
              closeEdit();
            }

          }}
        >

          <div className="tv-admin-edit-modal">

            <div className="tv-admin-edit-header">

              <div>

                <span>
                  TIME VALLEY ADMINISTRATION
                </span>

                <h2>
                  Edit <em>Timepiece.</em>
                </h2>

              </div>

              <button
                type="button"
                onClick={closeEdit}
                className="tv-admin-edit-close"
              >
                ×
              </button>

            </div>

            <form
              className="tv-admin-edit-form"
              onSubmit={handleUpdate}
            >

              {/* NAME */}

              <div className="tv-admin-edit-field">

                <label>
                  WATCH NAME
                </label>

                <input
                  type="text"
                  value={editName}
                  onChange={(e) =>
                    setEditName(
                      e.target.value
                    )
                  }
                  required
                />

              </div>

              <div className="tv-admin-edit-row">

                {/* PRICE */}

                <div className="tv-admin-edit-field">

                  <label>
                    PRICE
                  </label>

                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editPrice}
                    onChange={(e) =>
                      setEditPrice(
                        e.target.value
                      )
                    }
                    required
                  />

                </div>
{/* TOP / ORIGINAL PRICE */}

<div className="tv-admin-edit-field">

  <label>
    TOP / ORIGINAL PRICE
    <span
      style={{
        marginLeft: "6px",
        fontSize: "10px",
        opacity: 0.55,
        fontWeight: "400",
      }}
    >
      OPTIONAL
    </span>
  </label>

  <input
    type="number"
    step="0.01"
    min="0"
    value={editTopPrice}
    onChange={(e) =>
      setEditTopPrice(e.target.value)
    }
    placeholder="e.g. 80000"
  />

</div>
                {/* TAG */}

                <div className="tv-admin-edit-field">

                  <label>
                    TAG
                  </label>

                  <input
                    type="text"
                    value={editTag}
                    onChange={(e) =>
                      setEditTag(
                        e.target.value
                      )
                    }
                    placeholder="Signature Collection"
                  />

                </div>

              </div>

              {/* =================================================
                  WATCH COLLECTION
              ================================================= */}

              <div className="tv-admin-edit-field">

                <label>
                  WATCH COLLECTION
                </label>

                <select
                  value={editCollection}
                  onChange={(e) =>
                    setEditCollection(
                      e.target.value
                    )
                  }
                >

                  <option value="PREMIUM">
                    Premium Watches
                  </option>

                  <option value="CLASSIC">
                    Classic Watches
                  </option>

                  <option value="ESSENTIAL">
                    Essential Watches
                  </option>

                </select>

                <small>
                  Choose where this watch should appear.
                </small>

              </div>

              {/* DESCRIPTION */}

              <div className="tv-admin-edit-field">

                <label>
                  DESCRIPTION
                </label>

                <textarea
                  rows="5"
                  value={editDescription}
                  onChange={(e) =>
                    setEditDescription(
                      e.target.value
                    )
                  }
                />

              </div>

              {/* IMAGES */}

              <div className="tv-admin-edit-images">

                <div className="tv-admin-edit-images-heading">

                  <div>

                    <span>
                      PRODUCT IMAGERY
                    </span>

                    <h3>
                      Replace Images
                    </h3>

                  </div>

                  <small>
                    Leave empty to keep current image.
                  </small>

                </div>

                <div className="tv-admin-edit-image-grid">

                  {[
                    "image1",
                    "image2",
                    "image3",
                    "image4",
                  ].map(
                    (field, index) => {

                      const currentImage =
                        editingProduct[field];

                      return (

                        <label
                          className="tv-admin-edit-image-slot"
                          key={field}
                        >

                          <span>
                            IMAGE {index + 1}
                          </span>

                          <div className="tv-admin-edit-image-preview">

                            {editImages[field] ? (

                              <img
                                src={URL.createObjectURL(
                                  editImages[field]
                                )}
                                alt={`Preview ${
                                  index + 1
                                }`}
                              />

                            ) : currentImage ? (

                              <img
                                src={getImageUrl(
                                  currentImage
                                )}
                                alt={`Current ${
                                  index + 1
                                }`}
                              />

                            ) : (

                              <strong>
                                +
                              </strong>

                            )}

                          </div>

                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={(e) =>
                              handleImageChange(
                                field,
                                e.target.files[0]
                              )
                            }
                          />

                          <small>
                            Choose new image
                          </small>

                        </label>

                      );

                    }
                  )}

                </div>

              </div>

              {/* ACTIONS */}

              <div className="tv-admin-edit-actions">

                <button
                  type="button"
                  onClick={closeEdit}
                  className="tv-admin-edit-cancel"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="tv-admin-edit-save"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : "Save Changes →"}
                </button>

              </div>

            </form>

          </div>

        </div>

      )}

    </main>
  );
}

export default AdminCollections;